import * as React from 'react';
import Box from '@mui/material/Box';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CheckIcon from '@mui/icons-material/Check';
import { styled } from '@mui/material/styles';
import { ImageCard } from '../../../shared/imageCard/view/ImageCard';
import { IImageThumbnail } from '../../../Entities';
import { Translate } from '../../../../../../../shared/LanguageService';
import { useImageCardDragAndDrop } from '../state/use-image-card-drag-and-drop';
import { CardContainer } from './CardContainer';
import { ImageCategory } from '../../shared/state/utils';
import { shouldShowCategoryButton } from '../../imageCategoryChange/state/utils';
import { CategoryButton } from '../../imageCategoryChange/view/CategoryButton';
import { ImageSelectStitchSideButtons } from '../../imageStitching/view/ImageSelectStitchSideButtons';
import { canPlacePinOnImage } from '../../imagePin/state/utils';
import { preloadImage } from '../../../cache';
import { useApiUrl } from '../../../../../../../shared/hooks/useApiUrl';
import Tooltip from '@mui/material/Tooltip';

const DragHandle = styled(Box)({
    position: 'absolute',
    top: '6px',
    right: '6px',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    cursor: 'grab',
    opacity: 0,
    transition: 'opacity 0.15s ease',
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 1)'
    }
});

const SelectionIndicator = styled(Box)({
    position: 'absolute',
    top: '6px',
    right: '6px',
    zIndex: 2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: '#3D9970',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
    pointerEvents: 'none'
});

const CategoryButtonArea = styled(Box)({
    padding: '4px 12px 8px',
    display: 'flex',
    justifyContent: 'flex-end'
});

type DraggableImageCardProps = {
    image: IImageThumbnail;
    index: number;
    moveImage: (dragIndex: number, hoverIndex: number) => void;
    onDragEnd: () => void;
    onClick: () => void;
    onHover: () => void;
    isSelecting: boolean;
    isSelected: boolean;
    isSelectionFull: boolean;
    effectiveCategory: number;
    onToggleCategory: () => void;
    deliveredCount: number;
    orderedCount: number;
    isDraggable: boolean;
    isDragActive: boolean;
    photoStatus: number;
    isStitching: boolean;
    selectedStitchHeight: number | null;
    isPinning: boolean;
};

export function DraggableImageCard({
    image,
    index,
    moveImage,
    onDragEnd,
    onClick,
    onHover,
    isSelecting,
    isSelected,
    isSelectionFull,
    effectiveCategory,
    onToggleCategory,
    deliveredCount,
    orderedCount,
    isDraggable,
    isDragActive,
    photoStatus,
    isStitching,
    selectedStitchHeight,
    isPinning
}: DraggableImageCardProps) {
    let apiUrl = useApiUrl();

    let isPortrait = image.highResolutionHeight > image.highResolutionWidth;

    let isStitchHeightMismatch =
        isStitching &&
        isPortrait &&
        effectiveCategory !== ImageCategory.Extra &&
        selectedStitchHeight !== null &&
        image.highResolutionHeight !== selectedStitchHeight;

    let isStitchGreyedOut =
        isStitching &&
        (!isPortrait || effectiveCategory === ImageCategory.Extra || isStitchHeightMismatch);

    let showStitchButtons =
        isStitching &&
        isPortrait &&
        effectiveCategory !== ImageCategory.Extra &&
        !isStitchHeightMismatch;

    let isPinGreyedOut = isPinning && !canPlacePinOnImage(effectiveCategory, image);

    let showCategoryButton = shouldShowCategoryButton(
        isSelecting,
        isStitching || isPinning,
        photoStatus,
        effectiveCategory,
        image.isCustomerApproved
    );

    let { ref, drag, isDragging, isOver, canDrop } = useImageCardDragAndDrop({
        imageId: image.id,
        index,
        isSelecting,
        isDraggable,
        moveImage,
        onDragEnd
    });

    function preloadPreviewImage() {
        if (isSelecting && image.previewReference) {
            preloadImage(apiUrl + '/file/' + image.previewReference);
        } else if (!isSelecting) {
            onHover();
        }
    }

    let tooltipTitle = isStitchHeightMismatch
        ? Translate('image-processing/tooltip/stitch-images/not-same-height')
        : '';

    return (
        <Tooltip
            placement="top"
            title={tooltipTitle}
            arrow
            disableHoverListener={!isStitchHeightMismatch}
        >
            <CardContainer
                ref={ref}
                onMouseEnter={preloadPreviewImage}
                onContextMenu={(e) => e.preventDefault()}
                onClick={() => {
                    if (!isDragging) {
                        onClick();
                    }
                }}
                isDragging={isDragging}
                isSelected={isSelected}
                isSelecting={isSelecting}
                isSelectionFull={isSelectionFull}
                isOver={isOver}
                canDrop={canDrop}
                isDraggable={isDraggable}
                isDragActive={isDragActive}
                isStitchGreyedOut={isStitchGreyedOut || isPinGreyedOut}
            >
                {isDraggable && !isSelecting && !isStitching && !isPinning ? (
                    <DragHandle
                        ref={drag}
                        className="drag-handle"
                        onClick={function (e) {
                            e.stopPropagation();
                        }}
                    >
                        <DragIndicatorIcon sx={{ fontSize: 18, color: '#666' }} />
                    </DragHandle>
                ) : null}

                {isSelecting && isSelected ? (
                    <SelectionIndicator>
                        <CheckIcon sx={{ fontSize: 18, color: '#fff' }} />
                    </SelectionIndicator>
                ) : null}

                <Box sx={{ position: 'relative' }}>
                    <ImageCard
                        key={image.reference}
                        imageReference={image.reference}
                        isPortrait={isPortrait}
                        filename={image.filename}
                        category={effectiveCategory}
                        isSelected={isSelected}
                    />
                    {showStitchButtons ? <ImageSelectStitchSideButtons image={image} /> : null}
                </Box>
                <CategoryButtonArea>
                    <CategoryButton
                        variant="text"
                        size="small"
                        aria-hidden={!showCategoryButton}
                        tabIndex={showCategoryButton ? 0 : -1}
                        onClick={function (e) {
                            e.stopPropagation();
                            if (showCategoryButton) {
                                onToggleCategory();
                            }
                        }}
                        isVisible={showCategoryButton}
                        effectiveCategory={effectiveCategory}
                    >
                        {effectiveCategory === ImageCategory.Normal
                            ? Translate('imagethumbnail/remove')
                            : deliveredCount < orderedCount
                            ? Translate('imagethumbnail/add')
                            : Translate('imagethumbnail/buy')}
                    </CategoryButton>
                </CategoryButtonArea>
            </CardContainer>
        </Tooltip>
    );
}
